import { repairGermanProductUI } from '@theme/zentus-german-product-locale';
import { getMeasuringGuide, renderMeasuringGuide, getSizeChart, getDisplayValue } from '@theme/zentus-size-measuring';

/** Product-specific size guide. Measurements are an exact-SKU, dated theme snapshot. */
class ZentusSizeGuide extends HTMLElement {
  #controller;
  #data;
  #loading;
  #request;
  #error = false;
  #unit = 'cm';
  #draft;
  #pending = false;
  #selectionError = false;
  #overflow;
  #sizeNames;

  connectedCallback() {
    repairGermanProductUI(this);
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.dialog = this.querySelector('dialog');
    this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
    this.#sizeNames = new Map(this.rows.map(row => [row.optionId, row.size]));
    this.addEventListener('click', this.#click, options);
    this.dialog.addEventListener('close', this.#closed, options);
    this.dialog.addEventListener('keydown', this.#keyDown, options);
    this.closest('.shopify-section, dialog')?.addEventListener('variant:update', this.#update, options);
    this.closest('.shopify-section, dialog')?.addEventListener('variant:selected', this.#selecting, options);
    this.closest('.shopify-section, dialog')?.addEventListener('variant:error', this.#selectionFailed, options);
    // The ordinary help link remains usable when JavaScript is unavailable.
    this.querySelector('[data-open]')?.setAttribute('aria-haspopup', 'dialog');
    const pending = this._zentusPendingVariant;
    delete this._zentusPendingVariant;
    if (pending) this.#applyUpdate(pending.source);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.dialog?.open) {
      this.dialog.close();
      this.#restoreScroll();
    }
  }

  get rows() {
    return Array.from(this.querySelector('[data-state]').content.querySelectorAll('[data-size]'), node => ({ ...node.dataset }));
  }

  #click = (event) => {
    const target = event.target.closest('button, [data-open]');
    const row = event.target.closest('[data-pick-row]');
    if (target?.hasAttribute('data-open')) {
      event.preventDefault();
      this.#open();
    } else if (target?.hasAttribute('data-close')) {
      this.dialog.close();
    } else if (target?.hasAttribute('data-unit')) {
      this.#unit = target.dataset.unit === 'in' ? 'in' : 'cm';
      this.#render();
    } else if (target?.hasAttribute('data-pick')) {
      this.#draft = target.dataset.pick;
      this.#render();
      this.querySelector(`[data-pick="${CSS.escape(this.#draft)}"]`)?.focus({ preventScroll: true });
    } else if (row && !row.querySelector('button').disabled) {
      this.#draft = row.dataset.pickRow;
      this.#render();
      this.querySelector(`[data-pick="${CSS.escape(this.#draft)}"]`)?.focus({ preventScroll: true });
    } else if (target?.hasAttribute('data-apply')) {
      this.#apply();
    } else if (target?.hasAttribute('data-retry-variant')) {
      const retry = this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"] [data-variant-error] button`);
      if (retry) {
        this.#pending = true;
        this.#selectionError = false;
        retry.click();
        this.#render();
      } else {
        this.dialog.close();
      }
    } else if (target?.hasAttribute('data-retry')) {
      this.#load();
    } else if (event.target === this.dialog) {
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }
  };

  #open() {
    if (this.dialog.open) return;
    this.#draft = this.rows.find(row => row.selected === 'true')?.optionId;
    this.#overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.#load();
  }

  #keyDown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not(:disabled), summary, [tabindex="0"]')).filter(el => el.checkVisibility());
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  #restoreScroll() {
    if (this.#overflow !== undefined) {
      document.documentElement.style.overflow = this.#overflow;
      this.#overflow = undefined;
    }
  }

  #closed = () => {
    this.#restoreScroll();
    this.querySelector('[data-open]')?.focus({ preventScroll: true });
  };

  async #load() {
    if (!this.#supportsVendor()) {
      this.#data = undefined;
      this.#error = false;
      this.#render();
      return;
    }
    if (this.#data || this.#loading) {
      this.#render();
      return;
    }
    this.#loading = true;
    this.#error = false;
    this.#render();
    const request = this.#request = new AbortController();
    const timeout = setTimeout(() => request.abort(), 12000);
    const source = this.dataset.source;
    const vendor = this.dataset.vendor;
    try {
      const response = await fetch(source, { signal: request.signal, credentials: 'omit' });
      if (this.#request !== request || source !== this.dataset.source || vendor !== this.dataset.vendor) return;
      if (response.status === 404) {
        this.#data = { records: {} };
        return;
      }
      if (!response.ok) throw new Error('Size data unavailable');
      const data = await response.json();
      if (this.#request !== request || source !== this.dataset.source || vendor !== this.dataset.vendor) return;
      if (data.version !== 1 || String(data.productId) !== this.dataset.productId || !data.records) throw new Error('Unexpected size data');
      this.#data = data;
    } catch {
      if (this.#request === request) this.#error = true;
    } finally {
      clearTimeout(timeout);
      if (this.#request === request) {
        this.#request = undefined;
        this.#loading = false;
        if (this.isConnected) this.#render();
      }
    }
  }

  #selecting = (event) => {
    if (!event.target.closest(`variant-picker[data-product-id="${this.dataset.productId}"]`)) return;
    this.#pending = true;
    this.#selectionError = false;
    if (this.dialog.open) this.#render();
  };

  #selectionFailed = (event) => {
    if (String(event.detail?.productId) !== this.dataset.productId) return;
    this.#pending = false;
    this.#selectionError = true;
    if (this.dialog.open) this.#render();
  };

  #update = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    const source = event.detail.data.html?.querySelector(`zentus-size-guide[data-product-id="${this.dataset.productId}"]`);
    if (!source) return;
    this.#applyUpdate(source);
  };

  #applyUpdate(source) {
    const state = source.querySelector('[data-state]');
    if (!state) return;
    const sourceChanged = ['vendor', 'source'].some(key => this.dataset[key] !== source.dataset[key]);
    if (sourceChanged) {
      this.#request?.abort();
      this.#request = undefined;
      this.#loading = false;
      this.#data = undefined;
      this.#error = false;
      this.dataset.vendor = source.dataset.vendor || '';
      this.dataset.source = source.dataset.source || '';
    }
    // Variant responses occasionally contain another locale. Keep this page's
    // existing translations; colour changes only supply variant/measurement data.
    const nextState = state.cloneNode(true);
    for (const row of nextState.content.querySelectorAll('[data-size]')) {
      const name = this.#sizeNames.get(row.dataset.optionId);
      if (name !== undefined) row.dataset.size = name;
    }
    this.querySelector('[data-state]').replaceWith(nextState);
    const picker = this.closest('.shopify-section, dialog')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    for (const input of picker?.querySelectorAll('input[data-option-value-id]') || []) {
      const name = this.#sizeNames.get(input.dataset.optionValueId);
      if (name === undefined) continue;
      input.value = name;
      input.setAttribute('aria-label', name);
      const label = input.closest('label');
      if (label?.hasAttribute('title')) label.title = name;
      const text = label?.querySelector('.variant-option__button-label__text');
      if (text) text.textContent = name;
    }
    this.#pending = false;
    this.#selectionError = false;
    this.#draft = this.rows.find(row => row.selected === 'true')?.optionId;
    if (this.dialog.open) {
      if (sourceChanged) this.#load();
      else this.#render();
    }
  }

  #supportsVendor() {
    return this.dataset.vendor?.trim().toLowerCase() === 'roly' && Boolean(this.dataset.source);
  }

  #record(row) {
    if (!this.#supportsVendor() || !row?.variantId || !row.sku) return null;
    const record = this.#data?.records[row.sku];
    // Fail closed if the store changes since verification. Never fill from another colour.
    if (!record || record.raw !== row.raw || !['dimensions', 'reference'].includes(record.kind) || !Array.isArray(record.columns)) return null;
    if (!record.columns.length || record.columns.some(column => typeof column.key !== 'string' || typeof column.value !== 'string')) return null;
    return record;
  }

  #columnPrecision(records, key) {
    const values = records.flatMap(record => record?.columns.filter(column => column.key === key).map(column => column.value) || []);
    const matches = values.map(value => value.match(/^\d+(?:[.,](\d+))?$/));
    if (!matches.length || matches.some(match => !match)) return null;
    return Math.max(...matches.map(match => match[1]?.length || 0));
  }

  #format(value, kind, precision) {
    if (kind === 'reference') return value;
    if (this.#unit === 'cm') {
      if (precision !== null) return `${Number(value.replace(',', '.')).toFixed(precision)} cm`;
      return /cm/.test(value) ? value : `${value} cm`;
    }
    const converted = value.replace(/\d+(?:[.,]\d+)?/g, number => (Number(number.replace(',', '.')) / 2.54).toFixed(1));
    return /cm/.test(converted) ? converted.replace(/cm/g, 'in') : `${converted} in`;
  }

  #element(tag, text, className) {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = text;
    if (className) element.className = className;
    return element;
  }

  #render() {
    const previousFocus = this.dialog.contains(document.activeElement) ? document.activeElement : null;
    const rows = this.rows;
    const status = this.querySelector('[data-status]');
    const chart = this.querySelector('[data-chart]');
    const apply = this.querySelector('[data-apply]');
    const busy = this.#loading || this.#pending;
    status.replaceChildren();
    chart.replaceChildren();
    chart.setAttribute('aria-busy', String(busy));
    if (busy) status.textContent = this.labels.loading;
    else if (this.#selectionError) {
      status.append(this.#element('p', this.labels.selection_error));
      const retry = this.#element('button', this.labels.retry_selection, 'z-size-guide__retry');
      retry.type = 'button';
      retry.dataset.retryVariant = '';
      status.append(retry);
    } else if (this.#error) {
      status.append(this.#element('p', this.labels.error));
      const retry = this.#element('button', this.labels.retry, 'z-size-guide__retry');
      retry.type = 'button';
      retry.dataset.retry = '';
      status.append(retry);
    }
    const chosen = rows.find(row => row.optionId === this.#draft);
    const record = this.#record(chosen);
    const presentation = this.#data?.presentation;
    const records = rows.map(row => this.#record(row));
    const chartLayout = getSizeChart(presentation, records);
    const guidance = getMeasuringGuide(presentation, record);
    const measuring = this.querySelector('[data-measuring]');
    measuring.hidden = busy || this.#error || this.#selectionError || !this.#data?.presentation;
    if (!measuring.hidden) renderMeasuringGuide(measuring, guidance, this.labels);
    this.querySelector('[data-intro]').textContent = this.labels[chartLayout.sizesOnly ? 'measure_intro_sizes' : chartLayout.indicative ? 'measure_intro_indicative' : guidance.intro];
    const chartTitle = this.querySelector('[data-chart-title]');
    if (chartTitle) chartTitle.textContent = this.labels[chartLayout.sizesOnly ? 'choose' : chartLayout.indicative ? 'indicative_measurements' : 'measurements'];
    const aboutTitle = this.querySelector('[data-about-title]');
    if (aboutTitle) aboutTitle.textContent = this.labels[chartLayout.sizesOnly ? 'fit_advice' : 'about'];
    this.querySelector('[data-eyebrow]').textContent = presentation?.name ? [presentation.name, presentation.model].filter(Boolean).join(' · ') : (this.dataset.vendor || '').trim();
    const keys = chartLayout.sizesOnly ? [] : [...new Set(records.flatMap(record => record?.columns.map(column => column.key) || []))];
    const precision = Object.fromEntries(keys.map(key => [key, this.#columnPrecision(records, key)]));
    const units = this.querySelector('[data-unit-controls]');
    units.hidden = chartLayout.sizesOnly || busy || this.#selectionError || this.#error || !records.some(record => record?.kind === 'dimensions') || records.some(record => record?.kind === 'reference');
    if (units.hidden) this.#unit = 'cm';
    units.querySelectorAll('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === this.#unit)));
    if (!busy && !this.#error && !this.#selectionError) {
      const table = this.#element('table');
      if (chartLayout.sizesOnly) table.dataset.sizesOnly = '';
      const head = table.createTHead().insertRow();
      for (const key of ['size', ...keys]) {
        const th = this.#element('th', this.labels[chartLayout.labels[key]] || this.labels[key] || this.labels.measurements);
        th.scope = 'col';
        if (guidance.headerParts?.[key]) {
          th.replaceChildren();
          guidance.headerParts[key].forEach((part,index)=>{
            if (index) th.append(document.createTextNode(' × '));
            if (part.letter) th.append(this.#element('span',part.letter+' ','z-size-guide__letter'));
            th.append(document.createTextNode(this.labels[part.key] || this.labels.measurements));
          });
        } else if (guidance.headers[key]) {
          const letter = this.#element('span', guidance.headers[key] + ' ', 'z-size-guide__letter');
          th.prepend(letter);
        }
        head.append(th);
      }
      const body = table.createTBody();
      rows.forEach((row, index) => {
        const tr = body.insertRow();
        tr.dataset.pickRow = row.optionId;
        const chosen = row.optionId === this.#draft;
        if (chosen) tr.dataset.highlighted = 'true';
        if (row.selected === 'true') tr.setAttribute('aria-current', 'true');
        const th = this.#element('th');
        th.scope = 'row';
        const button = this.#element('button');
        button.append(this.#element('span', row.size));
        const check = this.#element('span', '✓', 'z-size-guide__check');
        check.setAttribute('aria-hidden', 'true');
        button.append(check);
        button.type = 'button';
        button.dataset.pick = row.optionId;
        button.disabled = !row.variantId || row.available !== 'true';
        button.setAttribute('aria-pressed', String(chosen));
        button.setAttribute('aria-label', `${this.labels.size} ${row.size}${button.disabled ? ` — ${this.labels.unavailable}` : ''}`);
        th.append(button);
        tr.append(th);
        if (chartLayout.sizesOnly) return;
        const record = records[index];
        if (!record) {
          const td = tr.insertCell();
          td.colSpan = keys.length;
          td.className = 'z-size-guide__missing';
          td.textContent = row.variantId ? this.labels.missing : this.labels.unavailable;
        } else {
          for (const key of keys) {
            const column = record.columns.find(item => item.key === key);
            const cell = tr.insertCell();
            cell.textContent = column ? this.#format(getDisplayValue(presentation, key, column.value), record.kind, precision[key]) : '—';
            if (column && precision[key] !== null) cell.className = 'z-size-guide__numeric';
          }
        }
      });
      chart.append(table);
    }
    this.querySelector('[data-chosen]').textContent = chosen?.size || this.labels.choose;
    const vendor = (this.dataset.vendor || '').trim();
    const profileNote = {footwear: 'footwear', headwear: 'headwear_advice', bag: 'bag_advice', set: 'set'}[presentation?.profile];
    const noteKey = profileNote || (chartLayout.sizesOnly || !record || record.note === 'missing' ? (vendor ? 'missing_vendor_note' : 'missing_note') : record.note);
    const note = this.labels[noteKey] || this.labels.missing_note;
    this.querySelector('[data-note]').textContent = note.replaceAll('[vendor]', () => vendor);
    apply.hidden = !chosen?.optionId;
    apply.disabled = busy || this.#selectionError || !chosen?.variantId || chosen.available !== 'true';
    // A retry or contextual refresh can replace the focused control. Keep focus
    // inside the modal without disturbing controls that remain in the document.
    if (this.dialog.open && previousFocus && !previousFocus.isConnected) {
      const next = this.querySelector('[data-retry-variant], [data-retry]') || this.querySelector('[data-pick][aria-pressed="true"]:not(:disabled)') || this.querySelector('[data-close]');
      next?.focus({ preventScroll: true });
    }
  }

  #apply() {
    const row = this.rows.find(item => item.optionId === this.#draft);
    if (this.#pending || this.#selectionError || !row?.variantId || row.available !== 'true') return;
    const picker = this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) || this.closest('.shopify-section')?.querySelector('variant-picker');
    const input = picker?.querySelector(`[data-option-value-id="${CSS.escape(row.optionId)}"]`);
    if (!input) return;
    this.dialog.close();
    if (input instanceof HTMLOptionElement) {
      input.parentElement.value = input.value;
      input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('zentus-size-guide')) customElements.define('zentus-size-guide', ZentusSizeGuide);
